import React from 'react'
import { observer } from 'mobx-react'
import {
  Form,
  FormColumn,
  FormColumns,
  FormGroupContainer,
  FormRow,
  NumericInputContainer,
  TextAreaContainer,
  TextInputContainer,
} from '../../../../components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '../../../../components/ModalSheet'
import Button from '../../../../components/Button'
import { BaseInventoryItemViewModel } from '../../views/BaseInventoryItemDialog/BaseInventoryItemViewModel'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import { match } from 'utils/pattern-match-util'
import {
  formatSupportingItemSpec,
  InventoryItemPricing,
  InventoryItemSupportingItem,
  InventoryItemType,
} from '../../InventoryItem'
import styles from './InventoryItemModalForm.sass'
import cx from 'classnames'
import ManageSupportingItemsModal from './ManageSupportingItemsModal'
import { FeeEditorViewModel } from 'admin/inventory/views/BaseInventoryItemDialog/FeeEditorViewModel'
import { FlatEditorViewModel } from 'admin/inventory/views/BaseInventoryItemDialog/FlatEditorViewModel'
import { MarkupEditorViewModel } from 'admin/inventory/views/BaseInventoryItemDialog/MarkupEditorViewModel'
import { MarkdownEditorViewModel } from 'admin/inventory/views/BaseInventoryItemDialog/MarkdownEditorViewModel'
import { ActionToggle } from 'components/ActionToggle'
import ExtensionFormFields from './ManageExtensionBillingInfo'
import ManageExtensionJobProgressionBillingModal from './ManageExtensionBillingInfo/JobProgressionBilingModal'

interface Props {
  vm: BaseInventoryItemViewModel
  title: string
  submitText: string
  overridden?: boolean
  children?: React.ReactNode
  description?: string | JSX.Element
  editing?: boolean
}

export default observer(
  ({ vm, submitText, title, overridden, children, editing }: Props) => (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={vm.showing}
      onRequestClose={vm.close}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>{title}</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <Form id="addItemForm" onSubmit={vm.submit}>
            {children}
            <FormRow>
              <FormGroupContainer label="Service type">
                <RadioButtonGroup size="big-wide">
                  <RadioButtonItem
                    onClick={() => vm.setItemType(InventoryItemType.Default)}
                    active={vm.isDefault}
                    disabled={editing || overridden}
                    dataTest="inventory-item-modal-type-default-button"
                  >
                    Default
                  </RadioButtonItem>
                  <RadioButtonItem
                    onClick={() => vm.setItemType(InventoryItemType.Tax)}
                    active={vm.isTax}
                    disabled={editing || overridden}
                    dataTest="inventory-item-modal-type-tax-button"
                  >
                    Tax
                  </RadioButtonItem>
                  <RadioButtonItem
                    onClick={() =>
                      vm.setItemType(InventoryItemType.Bookkeeping)
                    }
                    active={vm.isBookkeeping}
                    disabled={editing || overridden}
                    dataTest="inventory-item-modal-type-bookkeeping-button"
                  >
                    Bookkeeping
                  </RadioButtonItem>
                  <RadioButtonItem
                    onClick={() =>
                      vm.setItemType(InventoryItemType.Consultation)
                    }
                    active={vm.isConsultation}
                    disabled={editing || overridden}
                    dataTest="inventory-item-modal-type-consultation-button"
                  >
                    Consultation
                  </RadioButtonItem>
                  <RadioButtonItem
                    onClick={() => vm.setItemType(InventoryItemType.Extension)}
                    active={vm.isExtension}
                    disabled={editing || overridden}
                    dataTest="inventory-item-modal-type-extension-button"
                  >
                    Extension
                  </RadioButtonItem>
                </RadioButtonGroup>
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <TextInputContainer
                autoFocus
                label="Service item"
                field="name"
                placeholder="e.g. Schedule D"
                model={vm}
                error={vm.validation.getError('name')}
                data-test="inventory-item-modal-name-input"
                help={vm.remainingCharactersIndicator.name}
                disabled={overridden}
              />
            </FormRow>

            {vm.isConsultation && (
              <FormRow>
                <NumericInputContainer
                  label="Consultation length in minutes"
                  field="consultationLength"
                  help="The length of the consultation in minutes."
                  error={vm.validation.getError('consultationLength')}
                  model={vm}
                  data-test="inventory-item-modal-consultation-length-input"
                />
              </FormRow>
            )}

            <FormRow>
              <TextAreaContainer
                rows={4}
                label="Description"
                field="description"
                minLength={1}
                maxLength={280}
                help="A client friendly description for the item. Max 280 characters."
                model={vm}
                error={vm.validation.getError('description')}
                data-test="inventory-item-modal-description-input"
                disabled={overridden}
              />
            </FormRow>

            <FormRow>
              <NumericInputContainer
                label="Courtesy quantity"
                field="courtesyQuantity"
                help="Threshold before items counts towards the job price."
                error={vm.validation.getError('courtesyQuantity')}
                model={vm}
                data-test="inventory-item-modal-courtesy-amount-input"
              />
            </FormRow>

            <FormRow>
              <ActionToggle
                label="Hide from Pro"
                description="The Pro will not be able to see this service item within the job specs or job scope change request flow."
                value={vm.hideFromPro}
                onChange={() => vm.setHideFromPro(!vm.hideFromPro)}
                dataTest="inventory-item-modal-hide-from-pro-toggle"
              />
            </FormRow>

            {vm.isBookkeeping && (
              <FormRow>
                <ActionToggle
                  label="Recurring"
                  description="In the context of bookkeeping, this item will be added to the job at the end of every cycle when we run out of items to complete."
                  value={vm.recurring}
                  onChange={() => vm.setRecurring(!vm.recurring)}
                  disabled={editing || overridden}
                  dataTest="inventory-item-modal-bookkeeping-recurring-toggle"
                />
              </FormRow>
            )}

            {vm.isExtension && (
              <ExtensionFormFields
                billingVM={vm.extensionVM}
                editing={editing}
                overridden={overridden}
              />
            )}

            {!editing && vm.useRegularPricing && (
              <FormRow>
                <FormGroupContainer label={'Price type'}>
                  <RadioButtonGroup>
                    <RadioButtonItem
                      onClick={() =>
                        vm.setItemPriceType(InventoryItemPricing.Flat)
                      }
                      active={vm.isFlatPriceType}
                      dataTest="inventory-item-modal-price-type-flat-button"
                    >
                      Flat
                    </RadioButtonItem>
                    <RadioButtonItem
                      onClick={() =>
                        vm.setItemPriceType(InventoryItemPricing.Fee)
                      }
                      active={vm.itemPriceType === InventoryItemPricing.Fee}
                      disabled={vm.requiresFlatPricing}
                      dataTest="inventory-item-modal-price-type-fee-button"
                    >
                      Fee
                    </RadioButtonItem>
                    <RadioButtonItem
                      onClick={() =>
                        vm.setItemPriceType(InventoryItemPricing.Markup)
                      }
                      active={vm.itemPriceType === InventoryItemPricing.Markup}
                      disabled={vm.requiresFlatPricing}
                      dataTest="inventory-item-modal-price-type-markup-button"
                    >
                      Markup
                    </RadioButtonItem>
                    <RadioButtonItem
                      onClick={() =>
                        vm.setItemPriceType(InventoryItemPricing.Markdown)
                      }
                      active={
                        vm.itemPriceType === InventoryItemPricing.Markdown
                      }
                      disabled={vm.requiresFlatPricing}
                      dataTest="inventory-item-modal-price-type-markdown-button"
                    >
                      Markdown
                    </RadioButtonItem>
                  </RadioButtonGroup>
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.useRegularPricing &&
              match(vm.itemPriceType, {
                [InventoryItemPricing.Fee]: () => (
                  <FeeItemPriceInputs
                    vm={vm.pricingVMs[InventoryItemPricing.Fee]}
                  />
                ),
                [InventoryItemPricing.Flat]: () => (
                  <>
                    <FlatItemPriceInputs
                      vm={vm.pricingVMs[InventoryItemPricing.Flat]}
                      itemVm={vm}
                    />
                    {vm.isTax && <TaxReviewPriceInputs vm={vm} />}
                  </>
                ),
                [InventoryItemPricing.Markup]: () => (
                  <MarkupPriceInputs
                    vm={vm.pricingVMs[InventoryItemPricing.Markup]}
                  />
                ),
                [InventoryItemPricing.Markdown]: () => (
                  <MarkdownPriceInputs
                    vm={vm.pricingVMs[InventoryItemPricing.Markdown]}
                  />
                ),
              })}

            <FormRow>
              <FormGroupContainer>
                <label className={styles.supportingItemsLabel}>
                  <div className={styles.supportingItemRowHeader}>
                    Supporting items
                    <span className={styles.supportingItemCount}>
                      ({vm.supportingItems.length || 0})
                    </span>
                  </div>
                  <div
                    onClick={vm.manageSupportingItemsViewModel.show}
                    className={styles.supportingItemRowManage}
                  >
                    Manage
                  </div>
                </label>
                <div className={styles.supportingItemsContainer}>
                  {vm.supportingItems.length === 0 && <NoSupportingItems />}
                  {vm.supportingItems.map((item, index) => (
                    <SupportingItem key={index} supportingItem={item} />
                  ))}
                </div>
              </FormGroupContainer>
            </FormRow>
          </Form>
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            dataTest="inventory-item-modal-close-button"
            onClick={vm.close}
            type="default"
          >
            Close
          </Button>

          <Button
            submit
            form="addItemForm"
            type="primary"
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
            dataTest="inventory-item-modal-submit-button"
          >
            {submitText}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
      <ManageSupportingItemsModal vm={vm.manageSupportingItemsViewModel} />
      <ManageExtensionJobProgressionBillingModal
        vm={vm.extensionVM.progressionBillingVM.modalVM}
      />
    </ModalSheet>
  ),
)

const FeeItemPriceInputs = observer(function ({
  vm,
}: {
  vm: FeeEditorViewModel
}) {
  return (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Fee ($)"
            field="fee"
            model={vm}
            error={vm.validation.getError('fee')}
            data-test="inventory-item-modal-price-input"
            help="The fee to charge the client. Exempt from markup and markdown calculations."
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  )
})

const FlatItemPriceInputs = observer(function ({
  vm,
  itemVm,
}: {
  vm: FlatEditorViewModel
  itemVm: BaseInventoryItemViewModel
}) {
  return (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={2}
            label={itemVm.isTax ? 'Prep client price ($)' : 'Client price ($)'}
            field="clientAmount"
            model={vm}
            error={vm.validation.getError('clientAmount')}
            data-test="inventory-item-modal-price-input"
            help={
              itemVm.isTax
                ? 'The amount to charge the client for the tax prep.'
                : 'The amount to charge the client.'
            }
          />
        </FormColumn>

        <FormColumn>
          <NumericInputContainer
            precision={2}
            label={itemVm.isTax ? 'Prep Pro payout ($)' : 'Pro payout ($)'}
            error={vm.validation.getError('proPayoutAmount')}
            field="proPayoutAmount"
            model={vm}
            data-test="inventory-item-modal-pro-price-input"
            help={
              itemVm.isTax
                ? 'The amount to pay the Pro for the tax prep.'
                : 'The amount to pay the Pro.'
            }
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  )
})

const MarkupPriceInputs = observer(function ({
  vm,
}: {
  vm: MarkupEditorViewModel
}) {
  return (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Client markup (%)"
            field="clientMarkupRate"
            model={vm}
            error={vm.validation.getError('clientMarkupRate')}
            data-test="inventory-item-modal-price-input"
            help="The rate by which to mark up the job price for the client. Applied to the total job price."
          />
        </FormColumn>

        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Pro payout markup (%)"
            error={vm.validation.getError('proPayoutMarkupRate')}
            field="proPayoutMarkupRate"
            model={vm}
            data-test="inventory-item-modal-pro-price-input"
            help="The rate by which to mark up the Pro payout amount. Applied to the total Pro payout."
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  )
})

const MarkdownPriceInputs = observer(function ({
  vm,
}: {
  vm: MarkdownEditorViewModel
}) {
  return (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Client markdown (%)"
            field="clientMarkdownRate"
            model={vm}
            error={vm.validation.getError('clientMarkdownRate')}
            data-test="inventory-item-modal-price-input"
            help="The rate by which to mark down the job price for the client. Applied to the total job price."
          />
        </FormColumn>

        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Pro payout markdown (%)"
            error={vm.validation.getError('proPayoutMarkdownRate')}
            field="proPayoutMarkdownRate"
            model={vm}
            data-test="inventory-item-modal-pro-price-input"
            help="The rate by which to mark down the Pro payout amount. Applied to the total Pro payout."
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  )
})

const TaxReviewPriceInputs = observer(function ({
  vm,
}: {
  vm: BaseInventoryItemViewModel
}) {
  return (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Review client price ($)"
            field="reviewPrice"
            model={vm}
            error={vm.validation.getError('reviewPrice')}
            data-test="inventory-item-modal-review-price-input"
            help="The amount to charge the client for the review."
          />
        </FormColumn>

        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Review Pro payout ($)"
            error={vm.validation.getError('reviewPayout')}
            field="reviewPayout"
            model={vm}
            data-test="inventory-item-modal-review-pro-payout-input"
            help="The amount to pay the Pro for the review."
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  )
})

interface SupportingItemProps {
  supportingItem: InventoryItemSupportingItem
}

const SupportingItem = observer(function ({
  supportingItem,
}: SupportingItemProps) {
  return (
    <div className={styles.supportingItem}>
      {formatSupportingItemSpec(supportingItem.spec)}
      {supportingItem.required && (
        <span className={styles.required}>* required</span>
      )}
    </div>
  )
})

const NoSupportingItems = observer(function () {
  return (
    <div className={cx(styles.supportingItem, styles.noneAssigned)}>
      None assigned
    </div>
  )
})
